import { useState, useMemo } from 'react';
import { useApp } from '../context/useApp';
import { useAuth } from '../context/useAuth';
import { useOrganizations } from '../context/useOrganizations';
import { useLanguage } from '../i18n';
import { Plus, Trash2, ArrowRight, Briefcase, Crown, Zap, Building2 } from 'lucide-react';

interface DashboardProps {
  onCreateProject: () => void;
  onOpenProject: () => void;
  onOpenUpgrade?: () => void;
}

export default function Dashboard({ onCreateProject, onOpenProject, onOpenUpgrade }: DashboardProps) {
  const { projects, deleteProject, setActiveProjectId } = useApp();
  const { organizations } = useOrganizations();
  const { user } = useAuth();
  const { t } = useLanguage();
  const d = t.dashboard;
  const [filterOrgId, setFilterOrgId] = useState<string | null>(null);

  const filteredProjects = useMemo(() => {
    if (!filterOrgId) return projects;
    const org = organizations.find((o) => o.id === filterOrgId);
    const linkedIds = new Set(org?.linkedProjectIds || []);
    return projects.filter((p) => linkedIds.has(p.id));
  }, [projects, organizations, filterOrgId]);

  const openProject = (id: string) => {
    setActiveProjectId(id);
    onOpenProject();
  };

  const used = user?.generations_used ?? 0;
  const limit = user?.generations_limit ?? 0;

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 md:p-8 safe-bottom">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6 sm:mb-8 gap-3 sm:gap-6 flex-wrap sm:flex-nowrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-ink">{d.title}</h1>
            <p className="text-slate mt-1 text-sm sm:text-base max-w-xl">{d.subtitle}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 w-full sm:w-auto">
            {user && (
              <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-white border border-cloud">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate">{t.plans[user.plan] || user.plan}</span>
                <span className="text-sm text-ink">
                  {used.toLocaleString()} / {limit.toLocaleString()} {d.requests}
                </span>
              </div>
            )}
            <button
              onClick={onCreateProject}
              className="flex-1 sm:flex-none min-h-[44px] px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              {t.nav.newProject}
            </button>
          </div>
        </div>

        {user?.plan === 'starter' && onOpenUpgrade && (
          <div className="bg-red-50 border border-red-100 p-4 sm:p-5 mb-6 sm:mb-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 bg-white flex items-center justify-center border border-red-100 shrink-0">
                <Crown size={20} className="text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-ink mb-1">{t.project.upgradeTitle}</p>
                <p className="text-sm text-slate">{t.project.upgradeHint(t.landing.plans.founder.name)}</p>
              </div>
            </div>
            <button
              onClick={onOpenUpgrade}
              className="shrink-0 min-h-[44px] px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Zap size={16} />
              {t.project.upgradeNow}
            </button>
          </div>
        )}

        {organizations.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate mb-2">
              {d.filterLabel}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterOrgId(null)}
                className={`min-h-[36px] px-3 py-1.5 text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                  filterOrgId === null
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white text-ink border-cloud hover:bg-mist'
                }`}
              >
                <Briefcase size={14} />
                {d.filterAll}
                <span className={`text-xs ${filterOrgId === null ? 'text-white/80' : 'text-slate'}`}>
                  ({projects.length})
                </span>
              </button>
              {organizations.map((org) => {
                const linkedIds = new Set(org.linkedProjectIds || []);
                const count = projects.filter((p) => linkedIds.has(p.id)).length;
                if (count === 0) return null;
                const active = filterOrgId === org.id;
                return (
                  <button
                    key={org.id}
                    type="button"
                    onClick={() => setFilterOrgId(active ? null : org.id)}
                    className={`min-h-[36px] px-3 py-1.5 text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                      active
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-ink border-cloud hover:bg-mist'
                    }`}
                  >
                    <Building2 size={14} />
                    {org.name}
                    <span className={`text-xs ${active ? 'text-white/80' : 'text-slate'}`}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="bg-white border border-cloud p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Briefcase size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">{d.emptyTitle}</h2>
            <p className="text-slate mb-6 max-w-md mx-auto">{d.emptyDescription}</p>
            <button
              onClick={onCreateProject}
              className="min-h-[44px] px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
            >
              {d.emptyCta}
            </button>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-white border border-cloud p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Building2 size={32} className="text-red-500" />
            </div>
            <p className="text-slate mb-4">{d.filterEmpty}</p>
            <button
              type="button"
              onClick={() => setFilterOrgId(null)}
              className="min-h-[44px] px-4 py-2 text-ink hover:bg-mist font-medium border border-cloud"
            >
              {d.filterAll}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white border border-cloud p-4 sm:p-6 hover:border-red-300 hover:shadow-sm transition-all cursor-pointer group"
                onClick={() => openProject(project.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-red-50 flex items-center justify-center text-red-600 font-bold">
                    {project.name.charAt(0).toUpperCase()}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(d.deleteConfirm(project.name))) void deleteProject(project.id);
                    }}
                    className="p-2 text-ash hover:text-red-500 hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Delete project"
                    aria-label={`Delete ${project.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="font-semibold text-lg mb-1 truncate">{project.name}</h3>
                <p className="text-sm text-slate mb-4 line-clamp-2">{project.input.idea || 'No description'}</p>
                <div className="flex items-center justify-between text-xs text-ash border-t border-mist pt-3 gap-2">
                  <span className="font-medium truncate">{project.input.industry || d.industry}</span>
                  <span className="shrink-0">{new Date(project.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="mt-4 flex items-center text-sm font-medium text-red-500 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                  {d.open}
                  <ArrowRight size={16} className="ml-1" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
